import { mdiRadioboxBlank, mdiRadioboxMarked } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import { CustomerDetailModel } from "../../../../service/models/orders/customer_detail_model";
import { useModal } from "../../../../context_providers/modal/modal_context";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { Country } from "country-state-city";
import {
  AddressType,
  RegisteredofficeAddressModel,
} from "../../../../context_providers/auth/user_model";
import { dataTypes } from "../../../../utils/import_export_parser";
import RequiredMark from "../../../../ui/new_components/common/required_mark";
import { updateObject } from "../../../../utils/formDataToJson";
import { stateData } from "../../../../utils/states_data";
import { getMissingAddressFields } from "../../../../utils/location_util";
import {
  UpdateBillingAddressApi,
  updateDeliveryAddressApi,
} from "../../../../service/repos/address/address_repo";
import { useParams } from "react-router-dom";
import {
  matchStateName,
  PincodeHint,
  usePincodeCheck,
} from "../../../../ui/new_components/common/location/usePincodeCheck";

interface AddBillingProfileWidgetProps {
  //   address_type: "bill" | "delivery";
  //   type: string;
  registered_address?: RegisteredofficeAddressModel; // Consider specifying a proper type instead of `any`
  uid?: string;
  //   order_id?: any;
  //   billing?: CustomerDetailModel;
  type: string;
  onAdded: (data: CustomerDetailModel) => void;
  onEdited?: (data: CustomerDetailModel) => void;
  editProfile?: CustomerDetailModel | null;
  setRegisteredAddress: (data: RegisteredofficeAddressModel) => void;
  id_prefix?: string;
}

export interface AddressModel {
  id?: string;
  line1: string;
  line2?: string;
  line3?: string;
  city: string;
  state: string;
  country: string;
  pincode: number;
  type?: "Delivery" | "Billing";
}

const defaultAddress: AddressModel = {
  line1: "",
  line2: "",
  line3: "",
  city: "",
  state: "",
  country: "India",
  pincode: 0,
};
const defaultValue: any = {
  firstName: "",
  mobile: "",
  alt_mobile: "",
  email: "",
  address: defaultAddress,
};
export type CountryType = {
  isoCode: string;
  name: string;
};
const AddBillingWidget: FunctionComponent<AddBillingProfileWidgetProps> = ({
  onAdded,
  onEdited,
  editProfile,
  uid,
  registered_address,
  setRegisteredAddress,
  type,
  id_prefix = "add-billing",
  //   address_type,
}) => {
  const isEditMode = !!editProfile;
  const { setLoading } = useModal();
  const { showToast } = useToast();
  const { showModal, onClose } = useModal();
  const [customer, setCustomer] = useState<CustomerDetailModel>(
    editProfile
      ? {
          ...defaultValue,
          uid,
          branch: editProfile.branch,
          address: {
            line1: editProfile.address?.line1 || "",
            line2: editProfile.address?.line2 || "",
            line3: editProfile.address?.line3 || "",
            city: editProfile.address?.city || "",
            state: editProfile.address?.state || "",
            country: editProfile.address?.country || "India",
            pincode: editProfile.address?.pincode || 0,
          },
        }
      : {
          ...defaultValue,
          uid,
        },
  );
  const [same, setSame] = useState(!editProfile);
  const register_address = registered_address;
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [originalRegisteredAddress, setOriginalRegisteredAddress] =
    useState<RegisteredofficeAddressModel | null>(null);
  // "Same as registered" copies the registered address, so check that pincode instead.
  const pincodeCheck = usePincodeCheck({
    pincode: same
      ? originalRegisteredAddress?.pincode
      : customer?.address?.pincode,
    country: same
      ? originalRegisteredAddress?.country || "India"
      : customer?.address?.country,
    onFound: (d) =>
      setCustomer((prev: any) => ({
        ...prev,
        address: {
          ...prev.address,
          state:
            matchStateName(d.state, stateData.map((s) => s.name)) ?? d.state,
          city: d.city,
        },
      })),
  });

  const { id } = useParams<{ id: string }>();

  const [isMobile, setIsMobile] = useState(false);
  const typeLabel = type === AddressType.delivery ? "Delivery" : "Billing";

  useEffect(() => {
    if (registered_address && !originalRegisteredAddress) {
      setOriginalRegisteredAddress(registered_address);
    }
  }, [registered_address, originalRegisteredAddress]);

  const [selectedCountry, setSelectedCountry] = useState<string | null>(
    editProfile ? editProfile.address?.country || null : null,
  );
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);

  useEffect(() => {
    if (same && originalRegisteredAddress) {
      setRegisteredAddress(originalRegisteredAddress);
    }
  }, [same, originalRegisteredAddress, setRegisteredAddress]);

  const createBillingProfile = async () => {
    setLoading(true);

    const addressSource = same ? originalRegisteredAddress : customer.address;

    const addressToSave: AddressModel = {
      line1: addressSource?.line1 || "",
      line2: addressSource?.line2 || "",
      line3: addressSource?.line3 || "",
      city: addressSource?.city || "",
      state: addressSource?.state || "",
      country: addressSource?.country || "India",
      pincode: addressSource?.pincode || 0,
      type: typeLabel,
    };

    const requirePincode =
      type !== AddressType.billing || addressToSave.country === "India";
    const missingFields = getMissingAddressFields(addressToSave, {
      requirePincode,
    });
    if (missingFields.length) {
      showToast({
        type: ToastType.error,
        text: `${typeLabel} address: please fill ${missingFields.join(", ")}`,
      });
      setLoading(false);
      return;
    }
    const pincodeError = pincodeCheck.validate(addressToSave.state);
    if (pincodeError) {
      showToast({
        type: ToastType.error,
        text: `${typeLabel} address: ${pincodeError}`,
      });
      setLoading(false);
      return;
    }

    const updatedCustomer: CustomerDetailModel = {
      ...customer,
      address: addressToSave,
    };

    if (!id) {
      setCustomer(updatedCustomer);

      showToast({
        type: ToastType.success,
        text: `${typeLabel} address ${isEditMode ? "updated" : "added"}.`,
      });

      if (isEditMode) {
        onEdited?.(updatedCustomer);
      } else if (onAdded) {
        onAdded(updatedCustomer);
      }
    }
    // Update
    else {
      let res: any;

      if (type === AddressType.billing) {
        res = await UpdateBillingAddressApi({
          uid: customer.uid,
          type: type,
          address: addressToSave,
          branch: customer.branch,
        });
      } else if (type === AddressType.delivery) {
        res = await updateDeliveryAddressApi({
          uid: customer.uid,
          type: type,
          address: addressToSave,
          branch: customer.branch,
        });
      }

      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: "Updated Successfully",
        });
        if (isEditMode) {
          onEdited?.(updatedCustomer);
        }
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to update",
        });
      }
    }

    setLoading(false);
  };

  const handleChange = (e: any) => {
    const { value, name } = e.target;
    const type = e.target.getAttribute("data-type");
    const keys = name.split(".");
    const updatedValue = updateObject(customer, keys, value, type);
    setCustomer(updatedValue);
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <>
      <div
        id={`${id_prefix}-form`}
        className="flex flex-col gap-4 md:gap-8 text-sm"
      >
        <div className="flex flex-col gap-2">
          <div className="flex gap-2 items-center">
            <label htmlFor={`${id_prefix}-branch`}>Branch</label>
            <input
              id={`${id_prefix}-branch`}
              className="w-1/2 p-2 rounded border focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
              type="text"
              name="branch"
              placeholder="branch"
              value={customer.branch}
              onChange={(e) =>
                setCustomer((o) => ({
                  ...o!,
                  branch: e.target.value,
                }))
              }
              disabled={isEditMode}
              required
            />
          </div>

          <div className="flex items-center gap-1 my-2 md:my-4 ">
            <button
              id={`${id_prefix}-same-as-registered-toggle`}
              onClick={() => {
                setSame((o) => !o);
              }}
              className="flex gap-1 items-center"
            >
              <Icon
                className="text-teal"
                path={same ? mdiRadioboxMarked : mdiRadioboxBlank}
                size={0.8}
              />
              <p>Same as registered address</p>
            </button>
          </div>

          {same && (
            <section className="w-full py-2 md:py-4 bg-white rounded-md p-2 md:p-4 shadow-md border">
              {/* Address Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-6">
                {/* Left Column */}
                <div className="flex flex-col gap-4">
                  {/* Address Line 1 */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-line1`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Address
                    </label>
                    <input
                      required
                      type="text"
                      data-type={dataTypes.string}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-line1`}
                      name="address.line1"
                      value={register_address?.line1}
                      placeholder="Line 1"
                    />
                  </div>

                  {/* Address Line 2 */}
                  <div className="flex flex-col gap-1">
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-line2`}
                      name="address.line2"
                      value={register_address?.line2}
                      placeholder="Line 2"
                    />
                  </div>

                  {/* Address Line 3 */}
                  <div className="flex flex-col gap-1">
                    <input
                      type="text"
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-line3`}
                      name="address.line3"
                      value={register_address?.line3}
                      placeholder="Line 3"
                    />
                  </div>

                  {/* Country Selection */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-country`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Country
                    </label>
                    <input
                      id={`${id_prefix}-country`}
                      name="country"
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      value={register_address?.country || ""}
                    ></input>
                  </div>
                </div>

                {/* Right Column */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-state`}
                      className="text-sm font-medium text-gray-700"
                    >
                      State
                    </label>
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-state`}
                      name="state"
                      value={register_address?.state}
                      placeholder="State"
                    />
                  </div>

                  {/* City */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-city`}
                      className="text-sm font-medium text-gray-700"
                    >
                      City
                    </label>
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-city`}
                      name="city"
                      value={register_address?.city}
                      placeholder="City"
                    />
                  </div>

                  {/* Postal Code */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-pincode`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Postal Code
                    </label>
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-pincode`}
                      name="pincode"
                      value={register_address?.pincode}
                      placeholder="Postal Code"
                    />
                    <PincodeHint check={pincodeCheck} />
                  </div>
                </div>
              </div>
            </section>
          )}
          {!same && (
            <section className="w-full py-2 md:py-4 bg-white rounded-md p-2 md:p-4 shadow-md border">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2 md:gap-6">
                {/* Left Column */}
                <div className="flex flex-col gap-4">
                  {/* Address Line 1 */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-line1`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Address <RequiredMark />
                    </label>
                    <input
                      required
                      type="text"
                      data-type={dataTypes.string}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-line1`}
                      name="address.line1"
                      value={customer?.address?.line1}
                      onChange={handleChange}
                      placeholder="Line 1"
                    />
                  </div>

                  {/* Address Line 2 */}
                  <div className="flex flex-col gap-1">
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-line2`}
                      name="address.line2"
                      value={customer?.address?.line2}
                      onChange={handleChange}
                      placeholder="Line 2"
                    />
                  </div>

                  {/* Address Line 3 */}
                  <div className="flex flex-col gap-1">
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-line3`}
                      name="address.line3"
                      value={customer?.address?.line3}
                      onChange={handleChange}
                      placeholder="Line 3"
                    />
                  </div>

                  {/* Country Selection */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-country`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Country <RequiredMark />
                    </label>
                    <select
                      id={`${id_prefix}-country`}
                      name="country"
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      value={customer?.address?.country || ""}
                      onChange={(event) => {
                        const selectedCountryName = event.target.value;
                        if (selectedCountryName)
                          setSelectedCountry(selectedCountryName);
                        setCustomer((prev: any) => ({
                          ...prev,
                          address: {
                            ...prev?.address,
                            country: selectedCountryName,
                          },
                        }));
                      }}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.isoCode} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="flex flex-col gap-1">
                  {/* State Selection */}
                  {selectedCountry === "India" ? (
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor={`${id_prefix}-state`}
                        className="text-sm font-medium text-gray-700"
                      >
                        State <RequiredMark />
                      </label>
                      <select
                        id={`${id_prefix}-state`}
                        name="state"
                        className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                        value={customer?.address?.state}
                        onChange={(newValue) => {
                          const selectedStateValue = (
                            newValue.target as HTMLSelectElement
                          ).value;
                          setCustomer((prev) => ({
                            ...prev,
                            address: {
                              ...prev.address,
                              state: selectedStateValue
                                ? selectedStateValue
                                : "",
                            },
                          }));
                        }}
                      >
                        <option value="">Select State</option>
                        {stateData.map((state) => (
                          <option key={state.id} value={state.name}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor={`${id_prefix}-state`}
                        className="text-sm font-medium text-gray-700"
                      >
                        State <RequiredMark />
                      </label>
                      <input
                        type="text"
                        data-type={dataTypes.string}
                        className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                        id={`${id_prefix}-state`}
                        name="state1"
                        value={customer?.address?.state}
                        onChange={(e) => {
                          const selectedStateValue = e.target.value;
                          setCustomer((prev: any) => ({
                            ...prev,
                            address: {
                              ...prev.address,
                              state: selectedStateValue
                                ? selectedStateValue
                                : "",
                            },
                          }));
                        }}
                        placeholder="State"
                      />
                    </div>
                  )}

                  {/* City */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-city`}
                      className="text-sm font-medium text-gray-700"
                    >
                      City <RequiredMark />
                    </label>
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-city`}
                      name="city"
                      value={customer?.address?.city}
                      // onChange={handleChange}
                      onChange={(newValue) => {
                        const selectedCityValue = newValue.target.value;
                        setCustomer((prev: any) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            city: selectedCityValue ? selectedCityValue : "",
                          },
                        }));
                      }}
                      placeholder="City"
                    />
                  </div>

                  {/* Postal Code */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`${id_prefix}-pincode`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Postal Code{" "}
                      {(type !== AddressType.billing ||
                        customer?.address?.country === "India") && (
                        <RequiredMark />
                      )}
                    </label>
                    <input
                      required={
                        type !== AddressType.billing ||
                        customer?.address?.country === "India"
                      }
                      type="text"
                      data-type={dataTypes.number}
                      className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      id={`${id_prefix}-pincode`}
                      name="pincode"
                      value={customer?.address?.pincode}
                      onChange={(e) => {
                        const pincode = Number(e.target.value);
                        pincodeCheck.markTyped();
                        setCustomer((prev: any) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            pincode: pincode ? pincode : "",
                          },
                        }));
                      }}
                      placeholder="Postal Code"
                    />
                    <PincodeHint check={pincodeCheck} />
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
        <div className="col-span-2 flex justify-center ">
          <button
            id={`${id_prefix}-submit-btn`}
            onClick={() => createBillingProfile()}
            className="bg-myPrimaryColor font-semibold text-sm text-white rounded-3xl px-3 py-1 hover:scale-110 transform duration-300"
          >
            {isEditMode ? "Update" : "Submit"}
          </button>
        </div>
      </div>
    </>
  );
};

export default AddBillingWidget;
